<template>
  <div class="mail-status-modal" v-if="mailStatus">
    <p class="mail-status-modal__badge">
      <span class="badge" :class="'-' + statusColor">
        <FontAwesomeIcon :icon="'fa-light fa-' + statusIcon" aria-hidden="true" />
        <span class="ms-2">{{ statusLabel }}</span>
      </span>
    </p>
    <p class="mail-status-modal__who" v-if="recipient">{{ recipient }}</p>

    <dl class="mail-status-modal__fields">
      <template v-if="mailStatus.blockedAt">
        <dt class="mail-status-modal__label textStyle">{{ $t("mail_status.modal.blocked_at") }}</dt>
        <dd class="mail-status-modal__value">{{ blockedAtLabel }}</dd>
      </template>

      <dt class="mail-status-modal__label textStyle">{{ $t("mail_status.modal.cause") }}</dt>
      <dd>
        <pre class="mail-status-modal__raw">{{ causeLabel }}</pre>
      </dd>
    </dl>

    <AlertBlock class="mail-status-modal__hint" type="info" icon="circle-info" :text="hint" />

    <div class="mail-status-modal__actions">
      <button
        v-if="showCloseButton"
        type="button"
        class="btn btn-danger"
        :data-bs-dismiss="dismissible ? 'modal' : null"
        @click="$emit('close')"
      >
        {{ $t("close") }}
      </button>
      <button v-if="canUnblock && isBlocked" type="button" class="btn btn-success" :disabled="loading" @click="unblock">
        <FontAwesomeIcon class="me-2" icon="fa-light fa-unlock" aria-hidden="true" />
        {{ $t("mail_status.modal.unblock") }}
      </button>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import { mailStatusManager } from "@/services/mail-status/mail-status-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { dateFormatter } from "@/services/utilities/date-formatter";

export default {
  name: "MailStatusDetailComponent",
  components: { AlertBlock, FontAwesomeIcon },
  props: {
    mailStatus: {
      type: Object,
      default: null,
    },
    dismissible: {
      type: Boolean,
      required: false,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    /*
     * Défaut false : la route de déblocage est gardée par ROLE_STUDEA_ADMIN au niveau de la classe
     * MailController, donc seuls les écrans Admin peuvent l'activer. Les écrans self-service
     * (profil personnel, trinôme, fiche d'inscription) restent à false jusqu'à ST143-TA-4714, qui
     * tranchera le périmètre de rôle : y afficher le bouton n'aboutirait qu'à un 403.
     */
    canUnblock: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["unblocked", "close", "loading"],
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    isBlocked() {
      return mailStatusManager.isBlocked(this.mailStatus?.status);
    },
    hasIncident() {
      return mailStatusManager.hasIncident(this.mailStatus);
    },
    statusColor() {
      return mailStatusManager.getStatusColor(this.mailStatus?.status, this.hasIncident);
    },
    statusIcon() {
      return mailStatusManager.getStatusIcon(this.mailStatus?.status, this.hasIncident);
    },
    statusLabel() {
      return this.$t(mailStatusManager.getStatusLabelKey(this.mailStatus?.status, this.hasIncident));
    },
    hint() {
      if (this.isBlocked) {
        return this.$t("mail_status.modal.hint_blocked");
      }

      return this.hasIncident ? this.$t("mail_status.modal.hint_incident") : this.$t("mail_status.modal.hint_valid");
    },
    /*
     * Triple structuré uniquement : MailStatusView ne porte que dsn/category/code (§3.1 du brief
     * ST143-TA-4709 — le message brut du serveur distant, potentiellement sensible, est réservé au
     * logger applicatif et n'est jamais sérialisé).
     */
    causeLabel() {
      const cause = this.mailStatus?.cause;
      if (!cause) {
        return this.$t("mail_status.modal.no_cause");
      }

      const qualified = [
        cause.dsn ? `DSN ${cause.dsn}` : null,
        cause.category ? this.$t("mail_status.category." + cause.category) : null,
        cause.code ? this.$t("mail_status.code." + cause.code) : null,
      ].filter(Boolean);

      return qualified.length ? qualified.join(" — ") : this.$t("mail_status.modal.no_cause");
    },
    recipient() {
      if (!this.mailStatus) {
        return "";
      }

      return [this.mailStatus.name, this.mailStatus.email].filter(Boolean).join(" — ");
    },
    blockedAtLabel() {
      return this.formatDate(this.mailStatus?.blockedAt);
    },
  },
  methods: {
    formatDate(date) {
      return dateFormatter.canFormat(date) ? dateFormatter.format(date, "numeric_full") : "-";
    },

    async unblock() {
      this.loading = true;
      this.$emit("loading", true);
      try {
        const response = await mailStatusManager.unblock(this.mailStatus.email);
        if (response && !response.hasError) {
          notificationManager.showNotification("success", this.$t("mail_status.modal.unblock_success"));
          this.$emit("unblocked");
        } else {
          notificationManager.showNotification("error", this.$t("mail_status.modal.unblock_error"));
        }
      } catch {
        notificationManager.showNotification("error", this.$t("mail_status.modal.unblock_error"));
      } finally {
        this.loading = false;
        this.$emit("loading", false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mail-status-modal {
  &__badge {
    margin-bottom: 4px;
  }

  &__who {
    font-size: 13px;
    color: $m-color_7;
    margin-bottom: 18px;

    @include dark-theme {
      color: $m-color_6;
    }
  }

  &__fields {
    margin-bottom: 0;
  }

  &__label {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 5px;
  }

  &__value {
    font-size: 13px;
    margin-bottom: 14px;
  }

  dd:last-of-type {
    margin-bottom: 0;
  }

  &__raw {
    margin-bottom: 14px;
    background: $m-color_1;
    color: $m-color_9;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 12.5px;
    white-space: pre-wrap;
    word-break: break-word;

    @include dark-theme {
      background: $m-color_19;
      color: $m-color_3;
    }
  }

  &__hint {
    margin-top: 18px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
  }
}

.badge {
  display: inline-flex;
  align-items: center;

  &.-success {
    color: $d-success;
    background-color: $l-success;

    @include dark-theme {
      color: $m-color_19;
      background-color: $success;
    }
  }

  &.-warning {
    color: $d-warning;
    background-color: $l-warning;

    @include dark-theme {
      color: $m-color_19;
      background-color: $warning;
    }
  }

  &.-danger {
    color: $d-danger;
    background-color: $l-danger;

    @include dark-theme {
      color: $m-color_19;
      background-color: $danger;
    }
  }
}
</style>
